import {
  ChangeEvent,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Box, List, ListItem } from '@chakra-ui/react';
import { UseFormGetValues, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { AutocompleteItem } from 'types/autocompleteItem';
import { FieldWrapperPassThroughProps } from 'components/common/Form/FieldWrapper';
import { useBreakpoint } from 'hooks/utils/layout/useBreakpoint';
import { Breakpoints } from 'types/breakpoints';
import { Z_INDEX } from 'styles/zIndex';
import { getIsLastIndex } from 'utils/getIsLastIndex';
import { RHFFormInput } from './v2';

type AutocompleteFieldProps = FieldWrapperPassThroughProps & {
  zIndex?: number;
  placeholder?: string;
  getAutocompleteItems: (searchTerm: string) => Promise<AutocompleteItem[]>;
  registrationName: string;
  idName: string;
  setValue: UseFormSetValue<any>;
  register: UseFormRegister<any>;
  getValues: UseFormGetValues<any>;
  initialName?: string;
  initialId?: string;
  leftElement?: JSX.Element;
  rightElement?: JSX.Element;
  disabled?: boolean;
  disableCreate?: boolean;
  /**
   * `false` while the user is typing (value not yet chosen from the list).
   * `true` after picking a suggestion, Enter on one, or "Neu anlegen".
   */
  onConfirmedChange?: (confirmed: boolean) => void;
};

export const AutocompleteField = (props: AutocompleteFieldProps): ReactElement => {
  const {
    placeholder,
    label,
    getAutocompleteItems,
    registrationName,
    idName,
    register,
    setValue,
    getValues,
    initialId,
    initialName,
    leftElement,
    rightElement,
    disabled,
    zIndex,
    disableCreate,
    error,
    onConfirmedChange,
  } = props;

  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<AutocompleteItem[]>([]);
  const isDesktop = useBreakpoint(Breakpoints.DESKTOP);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState<number>(-1);

  const autoRef = useRef<HTMLDivElement>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null); // Reference to the debounce timer

  useEffect(() => {
    if (initialId && initialName) {
      setValue(idName, initialId);
      setValue(registrationName, initialName);
      onConfirmedChange?.(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-seed when initial ids change
  }, [initialId, initialName, setValue, idName, registrationName]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setValue(idName, null);
    onConfirmedChange?.(false);

    const userInput: string = e.target.value;

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(async () => {
      const suggestions = await getAutocompleteItems(userInput);
      setFilteredSuggestions(suggestions);
      setShowSuggestions(true);
    }, 300);
  };

  const handleClick = (
    e: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>,
    item?: AutocompleteItem
  ): void => {
    setSelectedSuggestionIndex(-1);
    setFilteredSuggestions([]);
    setShowSuggestions(false);
    onConfirmedChange?.(true);
    if (item) {
      setValue(registrationName, item?.name, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue(idName, item?.id, {
        shouldValidate: true,
        shouldDirty: true,
      });
    } else {
      setValue(registrationName, getValues(registrationName), {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    const suggestions = filteredSuggestions;
    if (e.key === 'ArrowDown' && selectedSuggestionIndex < suggestions.length - 1) {
      setSelectedSuggestionIndex(selectedSuggestionIndex + 1);
      if (selectedSuggestionIndex > 2) autoRef.current?.scrollBy(0, 55);
    } else if (e.key === 'ArrowUp' && selectedSuggestionIndex > -1) {
      setSelectedSuggestionIndex(selectedSuggestionIndex - 1);
      autoRef.current?.scrollBy(0, -50);
    } else if (e.key === 'Enter' && selectedSuggestionIndex > -1) {
      const selectedItem = suggestions[selectedSuggestionIndex];
      handleClick(e, selectedItem);
    }
  };

  const boxPos = isDesktop ? 'absolute' : 'relative';
  const dropdownLeft = isDesktop ? '-2px' : '0';
  const dropdownTop = '0';
  const dropdownPos = isDesktop ? 'relative' : 'absolute';
  const dropdownWidth = isDesktop ? '510px' : undefined;

  return (
    <Box width="100%" zIndex={zIndex}>
      <RHFFormInput
        disabled={disabled}
        type="text"
        label={label}
        placeholder={placeholder}
        name={registrationName}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          e.preventDefault();
          handleChange(e);
        }}
        leftElement={leftElement}
        onKeyDown={handleKeyDown}
        rightElement={rightElement}
        error={error}
      />
      {showSuggestions && getValues(registrationName) && (
        <Box position={boxPos} zIndex={Z_INDEX.SEARCH_BAR_SUGGESTIONS}>
          <Box
            ml={1}
            boxShadow="md"
            py="4"
            px="2"
            rounded="md"
            bg="grey.white"
            maxHeight="200"
            overflowY="auto"
            ref={autoRef}
            position={dropdownPos}
            left={dropdownLeft}
            top={dropdownTop}
            width={dropdownWidth}
            minWidth={dropdownWidth}
          >
            <List.Root gap="2">
              {filteredSuggestions.map((item, index, arr) => (
                <List.Item
                  whiteSpace="normal"
                  wordBreak="break-word"
                  key={item.id}
                  onClick={(e) => {
                    handleClick(e, item);
                  }}
                  cursor="pointer"
                  py="2"
                  px="3"
                  textStyle="body.s.regular"
                  borderBottom={
                    getIsLastIndex(index, arr) && disableCreate ? 'none' : '0.5px solid #E5E5E5'
                  }
                  color={selectedSuggestionIndex === index ? 'icon.accent' : 'text.secondary'} // Highlight selected suggestion
                >
                  {item.name}
                </List.Item>
              ))}
              {!disableCreate && (
                <ListItem onClick={handleClick} p="2" whiteSpace="normal" wordBreak="break-word">
                  Neu anlegen: {getValues(registrationName)}
                </ListItem>
              )}
              {filteredSuggestions.length === 0 && disableCreate && (
                <ListItem p="2" whiteSpace="normal" wordBreak="break-word">
                  Kein Eintrag gefunden
                </ListItem>
              )}
            </List.Root>
          </Box>
        </Box>
      )}
    </Box>
  );
};
